"use client";

import { SeoHeading } from "@/components/common/seo-heading";
import dynamic from "next/dynamic";
const RequestCallbackForm = dynamic(
  () => import("@/components/forms/request-callback-form").then((mod) => mod.RequestCallbackForm),
  { ssr: false }
);
import Logo from "@/components/logo";
import { Section } from "@/components/section";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Text } from "@/components/ui/text";
import { hasData } from "@/helpers/has-data";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { Icon } from "@/components/common/icon";
import { Suspense, useState } from "react";

type GetStartedTodayProps = {
  section: PageSections;
  data: {
    heading?: string;
    seoHeading?: string;
    description?: string;
    buttonActionText?: string;
    buttonActionUrl?: string;
  };
};

const GetStartedToday = ({ data, section }: GetStartedTodayProps) => {
  const [open, setOpen] = useState(false);

  if (!hasData(data)) return null;
  return (
    <Section
      className={`${section.sectionClass} dark:from-background bg-linear-to-r from-transparent from-10% to-slate-200 to-90% dark:to-transparent`}
      id={section.sectionId}
    >
      <Box className="grid grid-cols-12 items-center gap-5">
        <Box className="col-span-12 sm:col-span-7">
          <SeoHeading heading={data.heading} seoHeading={data.seoHeading} />
          <Text>{data.description}</Text>

          <Button variant="outline" onClick={() => setOpen(true)}>
            {data?.buttonActionText || "Request a callback"}
          </Button>
        </Box>
        <Box className="relative col-span-12 sm:col-span-5">
          <Logo className="absolute top-1/2 left-1/2 w-28 -translate-x-1/2 -translate-y-1/2" />
          <div className="mx-auto size-81.25">
            <div className="grid h-full grid-cols-3 grid-rows-4 place-content-center items-center justify-items-center">
              <div className="bg-card col-start-2 flex size-14 items-center justify-center self-end rounded-full shadow-md">
                <Icon name={getSystemIcon("SoftwareDevelopmentIcon")} className="text-primary" />
              </div>
              <div className="bg-card col-start-1 flex size-14 items-center justify-center rounded-full shadow-md">
                <Icon name={getSystemIcon("AutomobileIcon")} className="text-primary" />
              </div>
              <div className="bg-card col-start-3 flex size-14 items-center justify-center rounded-full shadow-md">
                <Icon name={getSystemIcon("EducationIcon")} className="text-primary" />
              </div>
              <div className="bg-card col-start-1 flex size-14 items-center justify-center rounded-full shadow-md">
                <Icon name={getSystemIcon("HealthcareIcon")} className="text-primary" />
              </div>
              <div className="bg-card col-start-3 flex size-14 items-center justify-center rounded-full shadow-md">
                <Icon name={getSystemIcon("ManufacturingIcon")} className="text-primary" />
              </div>
              <div className="bg-card col-start-2 flex size-14 items-center justify-center self-start rounded-full shadow-md">
                <Icon name={getSystemIcon("SupplyChainIcon")} className="text-primary" />
              </div>
            </div>
          </div>
        </Box>
      </Box>

      {/* Request callback popup */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 sm:max-w-2xl">
          <Suspense>
            <RequestCallbackForm />
          </Suspense>
        </DialogContent>
      </Dialog>
    </Section>
  );
};

export default GetStartedToday;
